import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchSystemLogs, type SystemLogEvent } from "../api/client";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, formatTime } from "../utils/datetime";
import DetailModal from "../components/DetailModal";

const LEVEL_OPTIONS = ["ALL", "ERROR", "WARNING", "INFO", "DEBUG"];
const COLLECTOR_OPTIONS = [
  "ALL",
  "workspaces",
  "activity",
  "ual",
  "privileges",
  "artifacts",
  "permissions",
  "pipelines",
  "scanner",
  "refresh",
  "refresh_schedules",
  "apps",
  "subscriptions",
  "imports",
  "dataflows",
  "pim",
  "security_alerts",
  "unknown",
];
const WINDOW_OPTIONS = [
  { label: "Last 15 min", value: 15 },
  { label: "Last 1 hour", value: 60 },
  { label: "Last 6 hours", value: 360 },
  { label: "Last 24 hours", value: 1440 },
];

function levelColor(level: string): string {
  switch (level.toUpperCase()) {
    case "ERROR": return "text-red-700 bg-red-50";
    case "WARNING": return "text-yellow-700 bg-yellow-50";
    case "INFO": return "text-blue-700 bg-blue-50";
    case "DEBUG": return "text-gray-600 bg-gray-50";
    default: return "text-gray-600 bg-gray-50";
  }
}

function levelDot(level: string): string {
  switch (level.toUpperCase()) {
    case "ERROR": return "bg-red-500";
    case "WARNING": return "bg-yellow-400";
    case "INFO": return "bg-blue-500";
    case "DEBUG": return "bg-gray-400";
    default: return "bg-gray-300";
  }
}

function LogRow({
  event,
  onOpenTrace,
}: {
  event: SystemLogEvent;
  onOpenTrace: (event: SystemLogEvent) => void;
}) {
  const dateFormat = useDateFormat();
  const formatTs = (ts: string) => formatDateTime(ts, dateFormat, true);

  const hasDetail = !!event.error;

  return (
    <div
      className={`border-b border-gray-100 px-4 py-2.5 hover:bg-gray-50 cursor-default ${
        event.level.toUpperCase() === "ERROR" ? "bg-red-50/40" : ""
      }`}
      onClick={() => hasDetail && onOpenTrace(event)}
    >
      <div className="flex items-start gap-3 min-w-0">
        {/* Level dot */}
        <span className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${levelDot(event.level)}`} />

        {/* Timestamp */}
        <span className="flex-shrink-0 text-xs text-gray-400 font-mono w-44">
          {formatTs(event.timestamp)}
        </span>

        {/* Level badge */}
        <span className={`flex-shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded ${levelColor(event.level)} w-16 text-center`}>
          {event.level.toUpperCase()}
        </span>

        {/* Collector badge */}
        {event.collector && event.collector !== "unknown" && (
          <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-mono max-w-[120px] truncate">
            {event.collector}
          </span>
        )}

        {/* Message */}
        <span className="flex-1 text-sm text-gray-800 break-words min-w-0 font-mono">
          {event.message}
        </span>

        {/* Trace affordance. The stack trace used to unfold here, between this
            row and the next, which pushed the rest of a three-hundred-row list
            down the page and cost the reader their place in it. It opens in a
            dialog now, so the list does not move and the trace gets the width
            a stack trace needs. */}
        {hasDetail && (
          <span className="ml-1 flex-shrink-0 text-xs font-medium text-blue-600 underline">
            Trace
          </span>
        )}
      </div>

      {/* Logger */}
      {event.logger && (
        <div className="ml-[7.5rem] mt-0.5 text-xs text-gray-400 font-mono pl-11">
          {event.logger}
        </div>
      )}
    </div>
  );
}

function TraceModal({
  event,
  onClose,
}: {
  event: SystemLogEvent;
  onClose: () => void;
}) {
  const dateFormat = useDateFormat();
  const parts = [
    formatDateTime(event.timestamp, dateFormat, true),
    event.level.toUpperCase(),
    event.collector && event.collector !== "unknown" ? event.collector : "",
    event.logger || "",
  ].filter(Boolean);

  return (
    <DetailModal title={event.message} subtitle={parts.join(" · ")} onClose={onClose}>
      <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border border-danger-border bg-danger-subtle p-3 text-xs text-danger-fg">
        {event.error}
      </pre>
    </DetailModal>
  );
}

export default function SystemLogsPage() {
  const dateFormat = useDateFormat();

  const [events, setEvents] = useState<SystemLogEvent[]>([]);
  const [traceEvent, setTraceEvent] = useState<SystemLogEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [queryStatus, setQueryStatus] = useState("");
  const [error, setError] = useState("");
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const [level, setLevel] = useState("ALL");
  const [collector, setCollector] = useState("ALL");
  const [minutes, setMinutes] = useState(60);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchSystemLogs({
        minutes,
        level: level === "ALL" ? undefined : level,
        collector: collector === "ALL" ? undefined : collector,
        search: search || undefined,
        limit: 300,
      });
      setEvents(res.events);
      setQueryStatus(res.queryStatus);
      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [level, collector, minutes, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(load, 30_000);
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [autoRefresh, load]);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
  }

  const errorCount = events.filter((e) => e.level.toUpperCase() === "ERROR").length;
  const warnCount = events.filter((e) => e.level.toUpperCase() === "WARNING").length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">System Logs</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Live collector and API logs from CloudWatch
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastRefreshed && (
            <span className="text-xs text-gray-400">
              Updated {formatTime(lastRefreshed, dateFormat, true)}
            </span>
          )}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              autoRefresh
                ? "bg-green-50 border-green-300 text-green-700"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {autoRefresh ? "Auto-refresh ON (30s)" : "Auto-refresh OFF"}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {loading ? (
              <>
                <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Querying…
              </>
            ) : (
              "Refresh"
            )}
          </button>
        </div>
      </div>

      {/* Summary chips */}
      {!loading && events.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
            {events.length} events
          </span>
          {errorCount > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-medium">
              {errorCount} errors
            </span>
          )}
          {warnCount > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 font-medium">
              {warnCount} warnings
            </span>
          )}
          {queryStatus && queryStatus !== "Complete" && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
              Query: {queryStatus}
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap gap-3 items-end shadow-sm">
        {/* Time window */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Time window</label>
          <select
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Level filter */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Level</label>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {LEVEL_OPTIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        {/* Collector filter */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Collector</label>
          <select
            value={collector}
            onChange={(e) => setCollector(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {COLLECTOR_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-gray-500">Search message</label>
          <form onSubmit={handleSearchSubmit} className="flex gap-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="e.g. GSI, 404, workspace…"
              className="flex-1 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(""); setSearchInput(""); }}
                className="px-2 py-1.5 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
              >
                ✕
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* Log table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Table header */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <span className="w-2" />
          <span className="w-36">Timestamp</span>
          <span className="w-16">Level</span>
          <span className="w-[120px]">Collector</span>
          <span className="flex-1">Message</span>
        </div>

        {loading && events.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm gap-2">
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Querying CloudWatch Logs Insights…
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-1">
            <svg className="w-8 h-8 text-gray-300 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
            </svg>
            No log events found for the selected filters
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[calc(100vh-22rem)] overflow-y-auto">
            {events.map((event, i) => (
              <LogRow
                key={`${event.timestamp}-${i}`}
                event={event}
                onOpenTrace={setTraceEvent}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer note */}
      <p className="text-xs text-gray-400 text-center pb-2">
        Queries CloudWatch Logs Insights — results may have a 1–2 min ingestion delay.
        Click any row marked “Trace” to read its full stack.
      </p>

      {traceEvent && (
        <TraceModal event={traceEvent} onClose={() => setTraceEvent(null)} />
      )}
    </div>
  );
}
